import { Field, ObjectType, Int, Float, ID } from 'type-graphql'

/**
 * Single search result from global search
 *
 * Represents a matched entity from any of the searchable modules.
 * Contains essential identifying information and relevance score.
 */
@ObjectType()
export class GlobalSearchResult {
  /**
   * Unique identifier of the entity
   */
  @Field(() => ID)
  _id: string

  /**
   * Entity code (e.g., "ORD-12345", "PRJ-001")
   * May be null for entities without a code field
   */
  @Field({ nullable: true })
  code?: string

  /**
   * Conversation external key, set on Chat rows only
   *
   * The detail route resolves threads on this key, never the database key,
   * so Chat navigation prefers it while every other module keeps using _id.
   */
  @Field({ nullable: true })
  externalId?: string

  /**
   * Entity name or title
   */
  @Field({ nullable: true })
  name?: string

  /**
   * Brief summary or description of the entity
   * Truncated to 100 characters in the UI
   */
  @Field({ nullable: true })
  summary?: string

  /**
   * Module type identifier
   * One of: Order, WorkOrder, Project, Task, Logistic, PurchaseOrder, Customer, Company
   */
  @Field()
  module: string

  /**
   * MongoDB text search relevance score
   * Higher score indicates better match to the search query
   * Used for ranking results within each module
   */
  @Field(() => Float)
  score: number
}

/**
 * Group of search results from a single module
 *
 * Results are grouped by module type to provide organized navigation.
 * Each group shows a subset of results with the total count for "View all" links.
 */
@ObjectType()
export class GlobalSearchResultGroup {
  /**
   * Module name
   * One of: Order, WorkOrder, Project, Task, Logistic, PurchaseOrder, Customer, Company
   */
  @Field()
  module: string

  /**
   * Total number of results found in this module
   * Used for "View all N results in Module" links
   */
  @Field(() => Int)
  count: number

  /**
   * Subset of results displayed for this module
   * Limited by smart distribution algorithm (typically 2 per module, up to 10 total)
   */
  @Field(() => [GlobalSearchResult])
  results: GlobalSearchResult[]
}

/**
 * Complete response from global search query
 *
 * Contains grouped results from all modules, total count, and performance metrics.
 * Results are distributed using a smart algorithm that ensures up to 10 total results
 * across all modules, with backfilling from modules that have more matches.
 */
@ObjectType()
export class GlobalSearchResponse {
  /**
   * Search results grouped by module
   * Modules with no results are excluded from the array
   */
  @Field(() => [GlobalSearchResultGroup])
  groups: GlobalSearchResultGroup[]

  /**
   * Total number of results across all modules
   * Sum of all group counts
   */
  @Field(() => Int)
  totalResults: number

  /**
   * Query execution time in milliseconds
   * Used for performance monitoring and debugging
   */
  @Field(() => Int, { nullable: true })
  executionTimeMs?: number
}
